<template>
  <div class="diff">
    <div
      v-for="person in diffPersons"
      :key="person.personId"
      class="data"
    >
      <basic-accordion class="data__accordion">
        <template #heading>
          <person-name
            class="lead-1"
            :person="person"
          />
          <!-- BIRTHDATE & GENDER -->
          <div
            :key="`person-data-${person.personId}`"
            class="person-data"
          >
            <div
              v-if="customerNumber(person)"
              class="person-data__entry"
            >
              <span
                class="title-2"
                v-text="$t('person.customerNumber')"
              />
              <span
                class="body-2"
                v-text="customerNumber(person)"
              />
            </div>
            <div class="person-data__entry">
              <span
                class="title-2"
                v-text="$t('person.birthdate')"
              />
              <span
                class="body-2"
                v-text="dateOfBirth(person.personData.dateOfBirth)"
              />
            </div>

            <div class="person-data__entry">
              <span
                class="title-2"
                v-text="$t('person.sex')"
              />
              <span
                class="body-2"
                v-text="$t(gender(person.personData.gender))"
              />
            </div>
          </div>
          <div class="columns-title lead-2">
            <span v-text="$t('screen.basket.existingCustomer.tabs.existing')" />
            <span
              class="highlight"
              v-text="$t('screen.basket.existingCustomer.tabs.changed')"
            />
          </div>
        </template>
        <template #default>
          <div
            v-for="(category, index) in person.diffProducts"
            :key="category.id"
            class="person-content"
          >
            <!-- CATEGORIES -->
            <div
              v-if="category.existing"
              :key="`${person.personId}-category-${category.id}-left`"
              class="products"
              :class="{ 'products--last': isLastCategory(index, person.diffProducts) }"
            >
              <the-product-card
                v-for="product in category.existing"
                :key="product.productId"
                read-only
                :category-id="category.id"
                :person="person"
                :product="product"
                :upselling="false"
              />
            </div>
            <div
              v-if="category.changed"
              :key="`${person.personId}-category-${category.id}-right`"
              class="products highlight"
              :class="{ 'products--last': isLastCategory(index, person.diffProducts) }"
            >
              <the-product-card
                v-for="product in category.changed"
                :key="product.productId"
                read-only
                :category-id="category.id"
                :person="person"
                :product="product"
                :upselling="false"
              />
            </div>
            <div
              v-else
              :key="`${person.personId}-category-missing-${category.id}-right`"
              class="products highlight"
            >
              <div class="category-missing">
                <span
                  class="category-title hidden"
                  v-html="$t(`categories.${category.id}.title`)"
                />
                <span
                  class="category-missing__text"
                  v-text="$t('screen.basket.diff.missingCategory')"
                />
              </div>
            </div>
          </div>
        </template>
      </basic-accordion>
      <div class="person__total">
        <basic-price
          class="person__total__price"
          :price="personTotal(person.existing)"
          price-class="h3"
        />
        <basic-price
          class="person__total__price total-right highlight"
          :price="personTotal(person.changed)"
          price-class="h3"
        />
      </div>
    </div>
    <div class="basket-total">
      <span
        class="basket-total__title h3"
        v-text="$t('basket.grandTotal')"
      />
      <div class="total__columns-title lead-2">
        <span v-text="$t('screen.basket.existingCustomer.tabs.existing')" />
        <span
          class="highlight"
          v-text="$t('screen.basket.existingCustomer.tabs.changed')"
        />
      </div>
      <div class="basket-total__price">
        <basic-price
          :price="existingTotal"
          price-class="h3"
        />
        <basic-price
          :price="changedTotal"
          class="highlight"
          price-class="h3"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, reactive, onMounted } from 'vue'

  import { BasketUtil } from '@shared/utils/Basket'
  import { DateTimeHelper } from '@shared/helpers'
  import { ReactivityUtil } from '@shared/utils/Reactivity'

  import useDiffBasket from '@shared/hooks/useDiffBasket'

  import TheProductCard from '@shared/components/Product/TheProductCard'
  import PersonName from '@shared/components/Person/PersonName'
  import { BasicAccordion, BasicPrice } from '@shared/components/Basic'

  // HOOKS
  const { basketPersons, contractPersons, diffPersons } = useDiffBasket()

  // INIT
  defineEmits(['close'])

  // DATA
  const accordions = reactive({})

  // COMPUTED
  const changedTotal = computed(() => {
    return basketPersons.value.reduce((sum, person) => sum + person.total, 0)
  })

  const existingTotal = computed(() => {
    return contractPersons.value.reduce((sum, person) => sum + person.total, 0)
  })

  // METHODS
  function customerNumber(person) {
    return person.contractNumber ? BasketUtil.getCustomerNumberFromContract(person.contractNumber) : null
  }

  function dateOfBirth(date) {
    return DateTimeHelper.formatDate(date)
  }

  function gender(personGender) {
    return `person.gender.${personGender.toLowerCase()}`
  }

  function isLastCategory(index, products) {
    return parseInt(index) === Object.keys(products).length - 1
  }

  function personTotal(person) {
    return person?.total || 0
  }

  // LIFECYCLE HOOKS
  onMounted(() => {
    ReactivityUtil.reAssign(
      accordions,
      diffPersons.value.reduce((acc, cur) => {
        return { ...acc, [cur.personId]: true }
      }, {})
    )
  })
</script>

<style name="mobile" scoped>
.diff {
  display: flex;
  flex-direction: column;
  row-gap: var(--dotcom-responsive-spacing-res-m);
  margin: var(--fixed-spacing-fix-06) 0;
}

.diff :deep(.price__suffix) {
  display: none;
}

.data {
  display: flex;
  flex-direction: column;
}

.data__accordion {
  padding: 0;
}

.data__accordion:deep(.accordion__header) {
  align-items: baseline;
}

.data__accordion:deep(.header__data),
.data__accordion:deep(.header__text) {
  flex-grow: 1;
}

.data__accordion:deep(.accordion__content) {
  margin: 0;
}

.columns-title {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-left: 21px; /* move title to right to compensate for close-icon space in header */

  & > span {
    padding: var(--fixed-spacing-fix-02);
  }
}

.columns-title .highlight {
  position: relative;
  padding: var(--fixed-spacing-fix-02) var(--fixed-spacing-fix-02) var(--fixed-spacing-fix-02)
    calc(var(--fixed-spacing-fix-02) + 21px);

  &::after {
    content: '';
    display: inline-block;
    position: absolute;
    top: 0;
    left: 100%;
    width: 21px; /* add the compensated margin in columns-title to fill the background */
    height: 100%;
    background-color: var(--surface-ext-a);
  }
}

.person__total {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: var(--fixed-spacing-fix-04);
}

.person__total__price {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: var(--fixed-spacing-fix-04);
}

.persons {
  grid-area: persons;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.person-details {
  display: flex;
  padding: var(--dotcom-responsive-spacing-res-m) var(--fixed-spacing-fix-06);
}

.person-details--left {
  position: relative;
  justify-content: space-between;
}

.person-details--right {
  justify-content: flex-end;
}

.person-data {
  padding: var(--fixed-spacing-fix-02) 0 var(--dotcom-responsive-spacing-res-xs);
}

.person-data__entry {
  display: flex;
  flex-direction: row;
  column-gap: var(--fixed-spacing-fix-02);
  align-items: center;
}

.no-person__message {
  margin: 0 20px;
}

.person-content {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.person-price {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.products {
  display: flex;
  flex-direction: column;
  row-gap: var(--fixed-spacing-fix-04);
  padding: 0 var(--fixed-spacing-fix-02) var(--fixed-spacing-fix-04);
}

.products:deep(.basket-product) {
  padding: var(--fixed-spacing-fix-04);
}

.products:deep(.product__price) {
  place-self: initial;
}

.products:deep(.price) {
  align-items: baseline;
}

.products:deep(.product__option) {
  flex-direction: column;
}

.products--last {
  padding: 0 var(--fixed-spacing-fix-02) var(--dotcom-responsive-spacing-res-m);
}

.total {
  padding: 24px;
  position: relative;

  & :deep(.total__title) {
    font-size: 20px;
  }

  & :deep(.total__price) {
    margin-right: 0;
    font-size: 20px;
  }
}

.close {
  align-self: center;
  margin-top: 24px;
  padding: 12px 24px;
}

.highlight {
  background-color: var(--surface-ext-a);
  padding-left: 8px;
}

.basket-total {
  display: flex;
  flex-direction: column;
}

.basket-total__title {
  margin: 0 0 var(--fixed-spacing-fix-04);
}

.total__columns-title {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border-bottom: 1px solid var(--on-surface);
  & > span {
    padding: var(--fixed-spacing-fix-02) var(--fixed-spacing-fix-02) var(--fixed-spacing-fix-02)
      calc(var(--fixed-spacing-fix-02) + 21px);
  }
}

.basket-total__price {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  & .price {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    padding: var(--fixed-spacing-fix-04);
  }
}
</style>
